import { useCallback, useEffect, useState } from "react";

export type ColumnSearchHistory = Record<string, string[]>;

export function normalizeFilterValue(value: string): string {
    return value.trim();
}

export function loadSearchHistory(storageKey: string): ColumnSearchHistory {
    if (typeof window === "undefined") return {};
    try {
        const raw = window.localStorage.getItem(storageKey);
        if (!raw) return {};
        const parsed = JSON.parse(raw) as Record<string, unknown>;
        const next: ColumnSearchHistory = {};
        for (const [col, values] of Object.entries(parsed)) {
            if (!Array.isArray(values)) continue;
            const normalized = values
                .filter((v): v is string => typeof v === "string")
                .map((v) => normalizeFilterValue(v))
                .filter((v, i, all) => v.length > 0 && all.indexOf(v) === i);
            if (normalized.length > 0) next[col] = normalized;
        }
        return next;
    } catch {
        return {};
    }
}

export function saveSearchHistory(storageKey: string, history: ColumnSearchHistory): void {
    if (typeof window === "undefined") return;
    try {
        window.localStorage.setItem(storageKey, JSON.stringify(history));
    } catch {
        // ignore
    }
}

export function removeFromHistory(history: ColumnSearchHistory, col: string, value: string): ColumnSearchHistory {
    const norm = normalizeFilterValue(value);
    const next = (history[col] ?? []).filter((v) => v !== norm);
    if (next.length === 0) {
        const { [col]: _removed, ...rest } = history;
        return rest;
    }
    return { ...history, [col]: next };
}

export interface UseColumnFilterReturn<TColumnKey extends string> {
    activeFilterColumn: TColumnKey | null;
    activeFilterAnchor: HTMLElement | null;
    filterInput: string;
    searchHistory: ColumnSearchHistory;
    setFilterInput: (value: string) => void;
    openColumnFilter: (column: TColumnKey, anchorEl: HTMLElement, initialValue?: string) => void;
    closeFilter: () => void;
    removeSaved: (value: string) => void;
    applyFilter: (value: string, saveToHistory?: boolean) => void;
}

export function useColumnFilter<TColumnKey extends string>(storageKey: string): UseColumnFilterReturn<TColumnKey> {
    const [activeFilterColumn, setActiveFilterColumn] = useState<TColumnKey | null>(null);
    const [activeFilterAnchor, setActiveFilterAnchor] = useState<HTMLElement | null>(null);
    const [filterInput, setFilterInput] = useState("");
    const [searchHistory, setSearchHistory] = useState<ColumnSearchHistory>({});

    useEffect(() => {
        setSearchHistory(loadSearchHistory(storageKey));
    }, [storageKey]);

    useEffect(() => {
        saveSearchHistory(storageKey, searchHistory);
    }, [storageKey, searchHistory]);

    const openColumnFilter = useCallback(
        (column: TColumnKey, anchorEl: HTMLElement, initialValue = "") => {
            if (activeFilterColumn === column) {
                setActiveFilterColumn(null);
                setActiveFilterAnchor(null);
                return;
            }
            setActiveFilterColumn(column);
            setActiveFilterAnchor(anchorEl);
            setFilterInput(initialValue);
        },
        [activeFilterColumn],
    );

    const closeFilter = useCallback(() => {
        setActiveFilterColumn(null);
        setActiveFilterAnchor(null);
    }, []);

    const removeSaved = useCallback(
        (value: string) => {
            if (!activeFilterColumn) return;
            setSearchHistory((prev) => removeFromHistory(prev, activeFilterColumn, value));
        },
        [activeFilterColumn],
    );

    const applyFilter = useCallback(
        (value: string, saveToHistory = true) => {
            if (!activeFilterColumn) return;
            const norm = normalizeFilterValue(value);
            if (saveToHistory && norm) {
                setSearchHistory((prev) => {
                    const existing = prev[activeFilterColumn] ?? [];
                    if (existing.includes(norm)) return prev;
                    return { ...prev, [activeFilterColumn]: [norm, ...existing].slice(0, 10) };
                });
            }
            setActiveFilterColumn(null);
            setActiveFilterAnchor(null);
        },
        [activeFilterColumn],
    );

    return { activeFilterColumn, activeFilterAnchor, filterInput, searchHistory, setFilterInput, openColumnFilter, closeFilter, removeSaved, applyFilter };
}
